import styles from './YoutubePostGroupBar.module.css';
import {
    PatchYoutubeInfluencerPostGroupBody,
    YoutubeInfluencerPostGroup,
    YoutubeInfluencerPostGroupStats,
} from '@round/api';
import cn from 'classnames';
import { ReactComponent as ArrowIcon } from 'assets/ArrowIcon.svg';
import { ReactComponent as EditIcon } from 'Modules/Advertising/assets/EditIcon.svg';
import { ReactComponent as TrashIcon } from 'assets/TrashIcon.svg';
import { ReactComponent as CopyIcon } from 'assets/CopyIcon.svg';
import { ProtectedByUserGroups } from 'SharedComponents/ProtectedByUserGroup/ProtectedByUserGroups';
import { getPostCountText, getWrappedUpText } from 'Modules/Advertising/InfluencerPlan/InfluencerPlan.helpers';
import { displayOptionalNumericTableValue, numberWithCommas, roundTo2Dp, showNotification } from 'helpers';
import { useContext, useState } from 'react';
import { patchYoutubeInfluencerPostGroup } from '@round/api';
import { useCheckUserGroupsAccess } from 'Modules/Auth/hooks/useCheckUserGroupsAccess';
import { getPostGroupPatchKeyErrorMessage } from '../../helpers';
import CurrencyInput from 'react-currency-input-field';
import { InfluencerPlanContext } from 'Modules/Advertising/InfluencerPlan/contexts/InfluencerPlanContext';
import ProgressBar from 'ui/ProgressBar/ProgressBar';
import { invoiceRequestStatusOptions } from 'Modules/Finance/InvoiceRequests/helpers';
import InvoiceRequestStatusBadge from 'Modules/Finance/InvoiceRequests/InvoiceRequestStatusBadge/InvoiceRequestStatusBadge';
import Button from 'ui/Buttons/Button/Button';
import { Popover } from '@round/ui-kit';
import Card from 'ui/Card/Card';
import { useLinkedProject } from '../../../Creators/components/CreatorbaseProjectSelect/hooks/useLinkedProject';
import ShareWithCreatorbaseToggle from '../../../InfluencerPlanGroupBar/ShareWithCreatorbaseToggle';
import useCreatorbaseProjectNavigationLink from '../../../Creators/useCreatorbaseNavigationLink';

type Props = {
    group: YoutubeInfluencerPostGroup;
    stats: YoutubeInfluencerPostGroupStats | null;
    isExpanded: boolean;
    onToggleExpand: (groupId: number) => void;
    //optional until we have the feature to create a post
    onCreatePost?: (groupId: number) => void;
    onEditBrief: (group: YoutubeInfluencerPostGroup) => void;
    updateGroup: typeof patchYoutubeInfluencerPostGroup;
    updateOrderingIndex: (groupId: number, currentIndex: number, targetIndex: number) => Promise<boolean | undefined>;
    onDeleteGroup: (group: YoutubeInfluencerPostGroup) => void;
    onCopyReportTable: (group: YoutubeInfluencerPostGroup) => void;
    hasLivePosts: boolean;
    refreshGroupPosts?: (groupId: number) => void;
    isGroupRefreshing?: boolean;
};

const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
};

const blurOnEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.currentTarget.blur();
    }
};

const YoutubePostGroupBar = ({
    group,
    stats,
    isExpanded,
    onToggleExpand,
    onCreatePost,
    onEditBrief,
    updateGroup,
    updateOrderingIndex,
    onDeleteGroup,
    hasLivePosts,
    onCopyReportTable,
    refreshGroupPosts,
    isGroupRefreshing,
}: Props) => {
    const canEdit = useCheckUserGroupsAccess(['influencer_editor']);
    const wrappedUpText = getWrappedUpText(group);
    const canEditGroup = canEdit && !wrappedUpText;
    const { influencerPlan } = useContext(InfluencerPlanContext);

    const { linkedProject } = useLinkedProject(influencerPlan?.application_manager_project_id);
    const { creatorbaseProject } = useCreatorbaseProjectNavigationLink(influencerPlan?.application_manager_project_id);
    const hasAccessToCreatorbaseProject = !!creatorbaseProject || !!linkedProject;

    const [groupName, setGroupName] = useState(group.name);
    const [budget, setBudget] = useState(group.budget);

    const [copyToClipboardButtonRef, setCopyToClipboardButtonRef] = useState<HTMLButtonElement | null>(null);

    const handleUpdateKey = async (updates: Partial<PatchYoutubeInfluencerPostGroupBody>) => {
        if (!canEditGroup) {
            return;
        }

        const response = await updateGroup(group.id, updates);

        if (response.status === 200) {
            showNotification('Group updated', 'info');
            return;
        }

        const errors = Object.entries(updates)
            .map(([key]) =>
                getPostGroupPatchKeyErrorMessage(response, key as keyof PatchYoutubeInfluencerPostGroupBody)
            )
            .filter(Boolean);

        throw new Error(errors.join(', '));
    };
    const handleUpdateOrderingIndex = async (targetIndex: number) => {
        try {
            const success = await updateOrderingIndex(group.id, group.ordering_index, targetIndex);

            if (success) {
                showNotification('Group order updated', 'info');
                return;
            }

            if (success === false) {
                showNotification('Failed to update group order', 'error');
                return;
            }
        } catch (e) {
            showNotification('Failed to update group order', 'error');
        }
    };

    const startDate = stats?.first_post_dt ? formatDate(new Date(stats.first_post_dt)) : '-';
    const endDate = stats?.last_post_dt ? formatDate(new Date(stats.last_post_dt)) : '-';
    const showRunDates = stats?.first_post_dt && stats?.last_post_dt;

    const postCountText = getPostCountText(stats?.post_count ?? 0, stats?.live_post_count ?? 0);

    const engagementRate = stats?.engagement_rate ? `${roundTo2Dp(stats.engagement_rate * 100)}%` : '-';

    const amountSpentPercent = stats?.amount_spent ? stats.amount_spent * 100 : 0;

    const cpmValue = stats?.total_cpm ? stats.total_cpm.toFixed(2) : '-';

    const currencySymbol = influencerPlan?.currency.symbol;

    const invoiceRequestStatus = invoiceRequestStatusOptions.find((o) => o.value === group.invoice_request_status);

    return (
        <article className={cn(styles.container, { [styles.refreshing]: isGroupRefreshing })}>
            <button
                data-test-id={`expand-group-${group.id}-button`}
                className={cn(styles.groupButton, styles.expandButton, {
                    [styles.expanded]: isExpanded,
                })}
                onClick={() => onToggleExpand(group.id)}
            >
                <ArrowIcon />
            </button>

            <div className={styles.infoColumns}>
                <div className={cn(styles.infoColumn, styles.groupName)}>
                    <input
                        className={cn(styles.heading, styles.input)}
                        type="text"
                        name="group-name"
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        onBlur={() => {
                            if (groupName === group.name) {
                                return;
                            }
                            handleUpdateKey({ name: groupName }).catch((e) => {
                                showNotification(e.message, 'error');
                                setGroupName(group.name);
                            });
                        }}
                        onKeyDown={blurOnEnterKey}
                        disabled={!canEditGroup}
                    />

                    <span className={styles.value}>{postCountText}</span>
                </div>

                <div className={cn(styles.infoColumn, styles.amountSpent)}>
                    <div className={cn(styles.value, styles.amountSpentContainer)}>
                        <CurrencyInput
                            className={styles.input}
                            prefix={currencySymbol}
                            value={budget}
                            onValueChange={(value) => setBudget(value ?? '')}
                            name="group-budget"
                            onBlur={() => {
                                if (budget === group.budget) {
                                    return;
                                }

                                handleUpdateKey({ budget: budget }).catch((e) => {
                                    showNotification(e.message, 'error');
                                    setBudget(group.budget);
                                });
                            }}
                            onKeyDown={blurOnEnterKey}
                            groupSeparator=","
                            decimalSeparator="."
                            disabled={!canEditGroup}
                        />
                        <span>{numberWithCommas(roundTo2Dp(amountSpentPercent))}%</span>
                    </div>
                    <ProgressBar
                        trackStyles={{
                            width: '100%',
                            backgroundColor: 'rgba(255, 255, 255, 0.4)',
                            height: '0.33rem',
                            marginTop: '0.33rem',
                        }}
                        barStyles={{
                            backgroundColor: 'white',
                        }}
                        progress={amountSpentPercent}
                    />
                </div>

                <div className={cn(styles.infoColumn, styles.cpm)}>
                    <span className={styles.heading}>{canEdit ? 'Client CPM' : 'CPM'}</span>
                    <span className={styles.value}>
                        {currencySymbol}
                        {displayOptionalNumericTableValue(cpmValue)}
                    </span>
                </div>

                <div className={styles.infoColumn}>
                    <span className={styles.heading}>Engagement Rate</span>
                    <span className={styles.value}>{engagementRate}</span>
                </div>

                <ProtectedByUserGroups groups={['influencer_editor']}>
                    <div className={cn(styles.infoColumn, styles.invoiceRequestStatus)}>
                        <span className={styles.heading}>Invoice Request Status</span>
                        <span className={styles.value}>
                            {invoiceRequestStatus ? (
                                <InvoiceRequestStatusBadge status={invoiceRequestStatus.value} />
                            ) : (
                                '-'
                            )}
                        </span>
                    </div>
                </ProtectedByUserGroups>

                <div className={cn(styles.infoColumn, styles.duration)}>
                    <span className={styles.heading}>Duration</span>
                    <span className={styles.value}>{showRunDates ? `${startDate} - ${endDate}` : '-'}</span>
                </div>

                <ProtectedByUserGroups groups={['user_admin', 'influencer_editor']}>
                    <ShareWithCreatorbaseToggle
                        isShared={group.is_shared_with_creatorbase}
                        onChange={async (isShared) => {
                            try {
                                await handleUpdateKey({
                                    is_hidden: !isShared,
                                    is_shared_with_creatorbase: isShared,
                                });
                                refreshGroupPosts?.(group.id);
                            } catch (e) {
                                showNotification(e instanceof Error ? e.message : 'Failed to update group', 'error');
                            }
                        }}
                        shouldDisable={!hasAccessToCreatorbaseProject || !!wrappedUpText}
                        warningMessage={
                            !hasAccessToCreatorbaseProject
                                ? 'Cannot share this group because the plan is not linked to a Creatorbase project.'
                                : undefined
                        }
                        unsharedText="In proposal"
                    />
                </ProtectedByUserGroups>

                {wrappedUpText && (
                    <div className={cn(styles.infoColumn, styles.wrappedUp)}>
                        <span className={styles.heading}>Wrapped up</span>
                        <span className={styles.value}>{wrappedUpText}</span>
                    </div>
                )}
            </div>

            <div className={styles.actionButtons}>
                <ProtectedByUserGroups groups={['influencer_editor']}>
                    {!wrappedUpText && (
                        <>
                            <button className={styles.groupButton} onClick={() => onEditBrief(group)}>
                                <EditIcon />
                                Edit brief
                            </button>

                            <button className={styles.groupButton} onClick={() => onCreatePost?.(group.id)}>
                                + New post
                            </button>

                            <button className={styles.groupButton} onClick={() => onDeleteGroup(group)}>
                                <TrashIcon />
                            </button>
                        </>
                    )}

                    <button ref={setCopyToClipboardButtonRef} className={styles.groupButton}>
                        <CopyIcon className={styles.icon} />
                    </button>

                    <Popover anchorElement={copyToClipboardButtonRef} showOn="click">
                        <Card className={styles.copyToTableDropdown}>
                            <Button onClick={() => onCopyReportTable(group)}>Report</Button>
                        </Card>
                    </Popover>

                    {!wrappedUpText && (
                        <div className={cn(styles.groupButton, styles.reorderingButtons)}>
                            <button
                                className={styles.reorderingButton}
                                onClick={() => handleUpdateOrderingIndex(group.ordering_index - 1)}
                            >
                                <ArrowIcon height={8} />
                            </button>
                            <button
                                className={cn(styles.reorderingButton, styles.increaseOrder)}
                                onClick={() => handleUpdateOrderingIndex(group.ordering_index + 1)}
                            >
                                <ArrowIcon height={8} />
                            </button>
                        </div>
                    )}
                </ProtectedByUserGroups>
            </div>
        </article>
    );
};

export default YoutubePostGroupBar;
